import { useMemo, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { chevronRight, clock, flashSlash } from 'assets/images';
import { TipoEstadoActualTrackingCorteEnum, TipoEstadoVistaDashboardEnum, TipoVarianteDetalleEnum } from 'config';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import { datoNoValido } from 'utils/colorBallsUtils';
import { CADENA_VACIA } from 'utils/constants';
import { formatTerDisplay, formatTimeOnly, SUFIJO_HORAS } from 'utils/formatters/dateFormatter';
import { buildTituloCorteProgramado, mapApiResponseToDashboardData } from 'utils/outageDataMapper';
import BotonCerrarDashboard from './BotonCerrarDashboard';
import DetalleAvance from './DetalleAvance';
import OutageClientesChart from './OutageClientesChart';
import OutageHorariosConDisclaimer from './OutageHorariosConDisclaimer';
import OutageReportarBox from './OutageReportarBox';
import useStyles from './OutageDashboardStyles';
import {
  anchoBarraProgreso,
  CANTIDAD_ETAPAS_PROGRAMADO,
  ESTADO_NODO_ACTIVO,
  ESTADO_NODO_HECHO,
  ESTADO_NODO_PENDIENTE,
  INDICE_INICIO_PROGRAMADO,
  INDICE_REPOSICION_PROGRAMADO,
  PROGRESO_COMPLETO,
  PROGRESO_MITAD,
  StepKind,
  TEST_ID_TIMELINE_PROGRAMADO,
  VARIABLE_ANCHO_PROGRESO,
  TEST_ID_AVANCE_PROGRAMADO,
  TEST_ID_BOTON_REPORTAR_PROGRAMADO,
  TEST_ID_CHART_PROGRAMADO,
  TEST_ID_DASHBOARD_PROGRAMADO,
  TEST_ID_DISCLAIMER_PROGRAMADO,
  TEST_ID_FOOTER_PROGRAMADO,
  TEST_ID_HORARIOS_PROGRAMADO,
  TEST_ID_MOTIVO_PROGRAMADO,
  TEST_ID_REPORTAR_PROGRAMADO,
  TEST_ID_TOGGLE_DETALLE_PROGRAMADO,
} from './outageDashboard.constants';
import { buildDatosGraficoClientes, buildFooterTexto, buildTerLabels } from './outageDashboard.helpers';
import useDetalleAvanceToggle from './useDetalleAvanceToggle';

/**
 * Dashboard de corte programado en curso.
 * @param data respuesta de tracking del corte
 * @param numeroOrden identificador de la orden
 * @param comuna comuna del polígono para el título
 * @param onDetalleAvanceChange avisa al modal cuando se abre el detalle
 */
const OutageDashboardProgramado = ({
  data,
  numeroOrden,
  comuna = CADENA_VACIA,
  onDetalleAvanceChange = undefined,
  onCerrar = undefined,
}: {
  data: TrackingCorteEstadosResponseModel;
  numeroOrden: string;
  comuna?: string;
  onDetalleAvanceChange?: (abierto: boolean) => void;
  onCerrar?: () => void;
}) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Estado que indica si el detalle de avance está abierto.
   */
  const { detalleAbierto, setDetalleAbierto } = useDetalleAvanceToggle(onDetalleAvanceChange);

  /**
   * Datos de vista del dashboard a partir de la respuesta de tracking.
   */
  const dashboard = useMemo(() => mapApiResponseToDashboardData(data, numeroOrden), [data, numeroOrden]);

  const terLabels = buildTerLabels(t);
  const inicioDisplay = formatTerDisplay(data.fechaInicioCorte, terLabels);
  const finDisplay = formatTerDisplay(data.fechaFinCorte, terLabels);

  const tituloMotivo = buildTituloCorteProgramado(comuna, {
    conComuna: c => t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.tituloMotivo', { comuna: c }),
    sinComuna: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.tituloMotivoSinComuna'),
  });

  const footerTexto = buildFooterTexto(data.fechaActualizacion, t);

  /** Programado pinta con estadoActual (confirmed/normalized), no con el secundario. */
  const esVerde = data.estadoActual === TipoEstadoActualTrackingCorteEnum.NORMALIZED;
  const totalPasos = CANTIDAD_ETAPAS_PROGRAMADO;
  const pasoActivoUi = esVerde ? INDICE_REPOSICION_PROGRAMADO : INDICE_INICIO_PROGRAMADO;
  const progressPct = esVerde ? PROGRESO_COMPLETO : PROGRESO_MITAD;

  /**
   * Determina si un paso está completado, activo o pendiente.
   * @param index posición del paso en el timeline
   * @returns estado visual del nodo
   */
  const stepKind = (index: number): StepKind => {
    if (esVerde) {
      return index < totalPasos - INDICE_INICIO_PROGRAMADO ? ESTADO_NODO_HECHO : ESTADO_NODO_ACTIVO;
    }
    if (index < pasoActivoUi) {
      return ESTADO_NODO_HECHO;
    }
    if (index === pasoActivoUi) {
      return ESTADO_NODO_ACTIVO;
    }
    return ESTADO_NODO_PENDIENTE;
  };

  const iconos = [flashSlash, flashSlash, clock];
  const horaEstado = esVerde ? formatTimeOnly(data.fechaFinCorte) : formatTimeOnly(data.fechaInicioCorte);
  const estadoDescTexto = esVerde
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.reposicionGradual')
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.estadoInicio');
  const avanceTitulo = esVerde
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.avanceReposicion')
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.avanceInicio');

  const { objetoGrafico, colores } = buildDatosGraficoClientes(data.porcentajeClientesAunSinServicio, data.porcentajeClientesRecuperados);

  const mostrarChart = dashboard.mostrarGraficoClientes;
  const avanceBoxClass = `${styles.avanceBox} ${esVerde ? styles.avanceBoxVerdeProgramado : styles.avanceBoxNaranja}`;
  const avanceTituloClass = `${styles.avanceTitulo} ${esVerde ? styles.avanceTituloVerde : styles.avanceTituloNaranja}`;
  const progressClass = `${styles.timelineLineProgress} ${esVerde ? styles.timelineLineProgressGreen : styles.timelineLineProgressNaranja}`;

  return (
    <div
      className={styles.root}
      data-testid={TEST_ID_DASHBOARD_PROGRAMADO}
      data-estado={esVerde ? TipoEstadoVistaDashboardEnum.NORMALIZADO : TipoEstadoVistaDashboardEnum.EN_CURSO}
    >
      {detalleAbierto ? (
        <DetalleAvance
          data={data}
          numeroOrden={numeroOrden}
          comuna={comuna}
          variante={TipoVarianteDetalleEnum.PROGRAMADO}
          onBack={() => setDetalleAbierto(false)}
          onCerrar={onCerrar}
        />
      ) : (
        <>
          <div className={styles.scrollArea}>
            <div className={styles.stickyCabecera}>
              <div className={styles.header}>
                <div className={styles.headerLeft}>
                  <span className={`${styles.statusDot} ${styles.statusDotNaranja}`} aria-hidden />
                  <p className={styles.statusLabel}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.enCurso')}</p>
                </div>
              </div>
              {onCerrar ? <BotonCerrarDashboard onCerrar={onCerrar} /> : null}
            </div>

            <div className={styles.content}>
              <div className={styles.body}>
                <div className={`${styles.leftCol} ${!mostrarChart ? styles.leftColFull : CADENA_VACIA}`}>
                  <h2 className={styles.motivo} data-testid={TEST_ID_MOTIVO_PROGRAMADO}>
                    {tituloMotivo}
                  </h2>

                  <OutageHorariosConDisclaimer
                    inicioDisplay={inicioDisplay}
                    finDisplay={finDisplay}
                    testIdHorarios={TEST_ID_HORARIOS_PROGRAMADO}
                    testIdDisclaimer={TEST_ID_DISCLAIMER_PROGRAMADO}
                  />

                  <div className={avanceBoxClass} data-testid={TEST_ID_AVANCE_PROGRAMADO}>
                    <p className={avanceTituloClass}>{avanceTitulo}</p>

                    <div className={styles.timelineRow} data-testid={TEST_ID_TIMELINE_PROGRAMADO}>
                      <div className={styles.timelineLineTrack} />
                      <div
                        className={progressClass}
                        style={{ [VARIABLE_ANCHO_PROGRESO]: anchoBarraProgreso(progressPct) } as CSSProperties}
                      />
                      {iconos.map((icono, index) => {
                        const kind = stepKind(index);
                        if (kind === ESTADO_NODO_HECHO) {
                          return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`step-${index}`} className={styles.stepNode}>
                              <span className={`${styles.stepDone} ${esVerde ? styles.stepDoneGreen : styles.stepDoneNaranja}`} />
                            </div>
                          );
                        }
                        const activeClass = esVerde ? styles.stepActiveGreen : styles.stepActiveNaranja;
                        const nodeClass = kind === ESTADO_NODO_ACTIVO ? activeClass : styles.stepPending;
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={`step-${index}`} className={`${styles.stepNode} ${nodeClass}`}>
                            <img src={icono} alt={CADENA_VACIA} className={styles.stepIcon} />
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.estadoActualWrap}>
                      {!datoNoValido(horaEstado) && <p className={styles.estadoHora}>{`${horaEstado}${SUFIJO_HORAS}`}</p>}
                      <p className={styles.estadoDesc}>{estadoDescTexto}</p>
                      <button
                        type="button"
                        className={styles.linkDetalle}
                        onClick={() => setDetalleAbierto(true)}
                        data-testid={TEST_ID_TOGGLE_DETALLE_PROGRAMADO}
                      >
                        {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.verDetalle')}
                        <img src={chevronRight} alt={CADENA_VACIA} className={styles.iconoLinkDetalle} aria-hidden />
                      </button>
                    </div>
                  </div>

                  {esVerde && (
                    <OutageReportarBox
                      textoInfo={t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.reportarInfo')}
                      testIdBox={TEST_ID_REPORTAR_PROGRAMADO}
                      testIdBoton={TEST_ID_BOTON_REPORTAR_PROGRAMADO}
                    />
                  )}
                </div>

                {mostrarChart && (
                  <OutageClientesChart
                    clientesSin={dashboard.clientes_sin_servicio}
                    clientesRec={dashboard.clientes_recuperados}
                    totalAfectados={dashboard.total_afectados}
                    objetoGrafico={objetoGrafico}
                    colores={colores}
                    testId={TEST_ID_CHART_PROGRAMADO}
                  />
                )}
              </div>
            </div>
          </div>

          <div className={styles.footer} data-testid={TEST_ID_FOOTER_PROGRAMADO}>
            <p className={styles.footerText}>{footerTexto}</p>
          </div>
        </>
      )}
    </div>
  );
};

OutageDashboardProgramado.defaultProps = {
  comuna: CADENA_VACIA,
  onDetalleAvanceChange: undefined,
  onCerrar: undefined,
};

export default OutageDashboardProgramado;
